import React from 'react';

import { Button } from '@eika/button';
import { ErrorCard } from '@eika/card';
import { Loader } from '@eika/loader';

import { useValidateBank, useValidateIban } from '../../fetchHooks/useCrossBorderBankValidation';
import type { CrossBorderReceiver } from '../../generated-types';
import useTranslation from '../../hooks/useTranslation';

interface Props {
    receiver: CrossBorderReceiver;
    receiverCountryName?: string;
}

const BankDetails: React.FC<Props> = ({ receiver, receiverCountryName }) => {
    const translated = useTranslation();

    const {
        data: ibanValidation,
        error: ibanValidationError,
        isLoading: loadingIbanValidation,
        refetch: retryIban,
    } = useValidateIban({ value: receiver.account.number }, receiver.account.type === 'IBAN');
    const {
        data: bankValidation,
        error: bankValidationError,
        isLoading: loadingBankValidation,
        refetch: retryBank,
    } = useValidateBank(
        receiver.bank.bic,
        receiver.bank.nationalId,
        receiver.bank.countryCode,
        receiver.account.type === 'BBAN',
    );

    const bankInformation = ibanValidation || bankValidation;

    if (ibanValidationError || bankValidationError) {
        return (
            <ErrorCard
                className="select-cross-border-receiver__fetch-receivers-error"
                title={translated.errors.somethingWentWrong}
                description={translated.crossBorderPayment.newReceiver.errorBankValidation}
            >
                <Button size="small" onClick={() => (ibanValidationError ? retryIban() : retryBank())}>
                    {translated.tryAgain}
                </Button>
            </ErrorCard>
        );
    }

    if (loadingBankValidation || loadingIbanValidation) {
        return <Loader className="cross-border-receiver-bank-details__loader" mini />;
    }

    const bic = bankInformation?.routingBicBank?.bic || bankInformation?.bank?.bic;

    return (
        <>
            <dl className="cross-border-receiver-details__description">
                <dt className="cross-border-receiver-details__term">
                    {receiver.account.type === 'IBAN'
                        ? translated.crossBorderPayment.newReceiver.ibanLabel
                        : translated.crossBorderPayment.newReceiver.bbanLabel}
                </dt>
                <dd className="cross-border-receiver-details__term-description">{receiver.account.number}</dd>
                {bic?.value && (
                    <>
                        <dt className="cross-border-receiver-details__term">
                            {translated.crossBorderPayment.newReceiver.bicLabel}
                        </dt>
                        <dd className="cross-border-receiver-details__term-description">
                            {bankInformation?.routingBicBank?.bic?.value || bankInformation?.bank?.bic?.value}
                        </dd>
                    </>
                )}
                <dt className="cross-border-receiver-details__term">
                    {translated.crossBorderPayment.newReceiver.receiverBank}
                </dt>
                <dd className="cross-border-receiver-details__bank-description">{bankInformation?.bank?.name}</dd>
                <dd className="cross-border-receiver-details__bank-description">{bankInformation?.bank?.address}</dd>
                <dd className="cross-border-receiver-details__bank-description">
                    {receiverCountryName?.toUpperCase()}
                </dd>
            </dl>
        </>
    );
};

export default BankDetails;
